import type { ChainModifiers, Entry, EntryFieldTypes, EntrySkeletonType, LocaleCode } from "contentful";
import type { TypeCardSkeleton } from "./TypeCard";
import type { TypeLargeCtaSkeleton } from "./TypeLargeCta";
import type { TypeTableOfContentsSettingSkeleton } from "./TypeTableOfContentsSetting";

/**
 * Fields type definition for content type 'TypeCardsSection'
 * @name TypeCardsSectionFields
 * @type {TypeCardsSectionFields}
 * @memberof TypeCardsSection
 */
export interface TypeCardsSectionFields {
    /**
     * Field type definition for field 'internalTitle' (Internal Title)
     * @name Internal Title
     * @localized false
     */
    internalTitle?: EntryFieldTypes.Symbol;
    /**
     * Field type definition for field 'header' (Header)
     * @name Header
     * @localized true
     */
    header?: EntryFieldTypes.Symbol;
    /**
     * Field type definition for field 'subHeader' (Sub Header)
     * @name Sub Header
     * @localized true
     */
    subHeader?: EntryFieldTypes.Symbol;
    /**
     * Field type definition for field 'backgroundColor' (Background Color)
     * @name Background Color
     * @localized false
     */
    backgroundColor: EntryFieldTypes.Symbol<"grey" | "light-blue" | "white">;
    /**
     * Field type definition for field 'cards' (Cards)
     * @name Cards
     * @localized false
     */
    cards?: EntryFieldTypes.Array<EntryFieldTypes.EntryLink<TypeCardSkeleton>>;
    /**
     * Field type definition for field 'tocSetting' (tocSetting)
     * @name tocSetting
     * @localized false
     */
    tocSetting?: EntryFieldTypes.EntryLink<TypeTableOfContentsSettingSkeleton>;
    /**
     * Field type definition for field 'bottomCta' (Bottom CTA)
     * @name Bottom CTA
     * @localized true
     */
    bottomCta?: EntryFieldTypes.EntryLink<TypeLargeCtaSkeleton>;
    /**
     * Field type definition for field 'perPage' (Per Page)
     * @name Per Page
     * @localized false
     */
    perPage?: EntryFieldTypes.Integer;
}

/**
 * Entry skeleton type definition for content type 'cardsSection' (Cards Section)
 * @name TypeCardsSectionSkeleton
 * @type {TypeCardsSectionSkeleton}
 * @author 7BjsgI9gzpO6Dv7BH5AOT6
 * @since 2018-03-25T23:58:44.958Z
 * @version 15
 */
export type TypeCardsSectionSkeleton = EntrySkeletonType<TypeCardsSectionFields, "cardsSection">;
/**
 * Entry type definition for content type 'cardsSection' (Cards Section)
 * @name TypeCardsSection
 * @type {TypeCardsSection}
 * @author 7BjsgI9gzpO6Dv7BH5AOT6
 * @since 2018-03-25T23:58:44.958Z
 * @version 15
 */
export type TypeCardsSection<Modifiers extends ChainModifiers, Locales extends LocaleCode = LocaleCode> = Entry<TypeCardsSectionSkeleton, Modifiers, Locales>;

export function isTypeCardsSection<Modifiers extends ChainModifiers, Locales extends LocaleCode>(entry: Entry<EntrySkeletonType, Modifiers, Locales>): entry is TypeCardsSection<Modifiers, Locales> {
    return entry.sys.contentType.sys.id === 'cardsSection'
}
